/**
 * bridgeConfig (web) — resolves the cross-device sync endpoint + token source
 * for the WebsocketSyncProvider. Returns null when VITE_SYNC_BRIDGE_URL is
 * unset, so cross-device sync stays OFF by default (cross-tab BroadcastChannel
 * keeps working) and nothing changes until the bridge is deployed + configured.
 *
 * `getToken(siteId)` mints a short-lived, site-scoped bridge token through the
 * webapp's BFF (`POST /api/sites/:id/bridge-token`): the openauth token lives
 * in an HttpOnly cookie, so the server attaches it, and builder-api refuses
 * anyone who is not an owner or editor of the site.
 *
 * `VITE_SYNC_BRIDGE_AUTH=required` says the bridge refuses a handshake without
 * a token (production). Only cloud sites (UUID ids) can get one, so local
 * drafts then stay off the bridge instead of retrying a doomed handshake. A
 * dev bridge runs without auth, and local drafts sync through it as before.
 */
import { createBridgeTokenClient } from "./net/bridgeTokenClient.js";

export interface BridgeConfig {
  readonly url: string;
  readonly getToken?: (siteId: string) => Promise<string | null>;
  /** The bridge refuses tokenless handshakes. */
  readonly requiresToken: boolean;
}

const CLOUD_SITE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether `siteId` is a server-owned site, the only kind that can be minted
 *  a bridge token. */
export function isCloudSiteId(siteId: string): boolean {
  return CLOUD_SITE_ID.test(siteId);
}

/**
 * No-op on web (parity with bridgeConfig.native's registry, so the barrel export
 * resolves on both targets). The web app authenticates the bridge through its
 * server-side session/BFF, not a registered device-token provider.
 */
export function setBridgeTokenProvider(
  _fn: (siteId: string) => Promise<string | null>,
): void {
  /* web: BFF/session-based; no device provider */
}

let tokenClient: ReturnType<typeof createBridgeTokenClient> | null = null;

export function getBridgeConfig(): BridgeConfig | null {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  const url = env?.["VITE_SYNC_BRIDGE_URL"];
  if (!url) return null;
  tokenClient ??= createBridgeTokenClient({
    apiBase: "",
    path: (siteId) => `/api/sites/${encodeURIComponent(siteId)}/bridge-token`,
    credentials: "same-origin",
  });
  const client = tokenClient;
  return {
    url,
    requiresToken: env?.["VITE_SYNC_BRIDGE_AUTH"] === "required",
    getToken: (siteId) =>
      isCloudSiteId(siteId)
        ? client.getBridgeToken(siteId).catch(() => null)
        : Promise.resolve(null),
  };
}
